"use client"

import { cn } from "@/lib/utils"

interface SkeletonProps {
  className?: string
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-bg-alt",
        className
      )}
    />
  )
}

// Post list skeleton
export function PostListSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-bg-alt border-b border-border">
          <tr>
            <th className="text-left px-4 py-3 text-sm font-medium text-text">
              Title
            </th>
            <th className="text-left px-4 py-3 text-sm font-medium text-text hidden md:table-cell">
              Status
            </th>
            <th className="text-left px-4 py-3 text-sm font-medium text-text hidden lg:table-cell">
              Tags
            </th>
            <th className="text-left px-4 py-3 text-sm font-medium text-text hidden sm:table-cell">
              Date
            </th>
            <th className="text-right px-4 py-3 text-sm font-medium text-text">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              <td className="px-4 py-3">
                <Skeleton className="h-5 w-48 mb-2" />
                <Skeleton className="h-4 w-64" />
              </td>
              <td className="px-4 py-3 hidden md:table-cell">
                <Skeleton className="h-6 w-20 rounded-full" />
              </td>
              <td className="px-4 py-3 hidden lg:table-cell">
                <div className="flex gap-1">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-5 w-12 rounded-full" />
                </div>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell">
                <Skeleton className="h-4 w-24" />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Post form skeleton
export function PostFormSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content - Editor */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <Skeleton className="h-4 w-20 mb-2" />
            {/* Toolbar skeleton */}
            <div className="flex flex-wrap items-center gap-2 p-2 border border-border rounded-t-lg bg-bg-alt/50">
              {[...Array(12)].map((_, i) => (
                <Skeleton key={i} className="h-8 w-8 rounded" />
              ))}
            </div>
            {/* Editor area skeleton */}
            <div className="border border-t-0 border-border rounded-b-lg p-4 min-h-[400px]">
              <Skeleton className="h-6 w-3/4 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </div>
        </div>

        {/* Sidebar - Post Settings */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <Skeleton className="h-5 w-28 mb-4" />

            <div className="space-y-4">
              {/* Title */}
              <div>
                <Skeleton className="h-4 w-12 mb-1" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>

              {/* Slug */}
              <div>
                <Skeleton className="h-4 w-10 mb-1" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>

              {/* Excerpt */}
              <div>
                <Skeleton className="h-4 w-16 mb-1" />
                <Skeleton className="h-20 w-full rounded-lg" />
              </div>

              {/* Author */}
              <div>
                <Skeleton className="h-4 w-14 mb-1" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>

              {/* Tags */}
              <div>
                <Skeleton className="h-4 w-10 mb-1" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>

              {/* Published checkbox */}
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
